import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { LibraryBooksOutlined } from '@mui/icons-material'
import type { ReachComponent } from '../services/api'

/**
 * What this agent retrieves from.
 *
 * The card already names the agent's model, its tools and the *kind* of memory
 * it keeps — and never which store that memory actually is. That gap is the
 * whole reason Agent Reach exists, and it belongs here rather than on a page
 * of its own: somebody reading an agent card is already asking what this agent
 * touches.
 *
 * Cross-repository is the part no other surface can show. A store declared in
 * an infrastructure repository and retrieved from here is invisible to a scan
 * of either repository alone, so it is called out explicitly rather than left
 * for a reader to infer from two lists.
 */

const KIND_LABELS: Record<string, string> = {
  vector_store: 'vector store',
  search_engine: 'search engine',
  graph_store: 'graph store',
  managed_kb: 'knowledge base',
  object_store: 'object store',
  hosted_store: 'hosted store',
  embedding_model: 'embedding model',
  mcp_server: 'MCP server',
  a2a_endpoint: 'A2A endpoint',
  retrieval_endpoint: 'retrieval endpoint',
  inference_endpoint: 'inference endpoint',
  model_artifact: 'model artifact',
}

/** An endpoint we hashed rather than transmitted. Worth saying so plainly. */
const isDigest = (identity: string) => identity.startsWith('ep:')

const shortIdentity = (identity: string) =>
  isDigest(identity) ? `${identity.slice(0, 11)}…` : identity

/**
 * Kinds an agent talks to over a protocol rather than retrieves knowledge
 * from. They already have their own chips on this card, from
 * AgentConnectivityChips — repeating them here is the duplication this whole
 * redesign existed to remove, and on an estate with a workspace-level MCP
 * config every agent carries the identical set, which buries the two or three
 * chips that actually differ between agents.
 */
const PROTOCOL_KINDS = new Set(['mcp_server', 'a2a_endpoint'])

const AgentReaches = ({ components }: { components: ReachComponent[] }) => {
  const knowledge = components.filter(c => !PROTOCOL_KINDS.has(c.component_kind))

  // Absent means we have nothing to say, not that the agent reaches nothing.
  // A row reading "reaches: none" would be a claim, and on a repository whose
  // knowledge layer is declared elsewhere it would be a false one.
  if (!knowledge.length) return null

  const classified = knowledge.filter(c => c.classification)

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        <LibraryBooksOutlined sx={{ fontSize: 13, verticalAlign: 'text-bottom', mr: 0.5 }} />
        Retrieves from
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {knowledge.map(c => {
          const label = KIND_LABELS[c.component_kind] || c.component_kind.replace(/_/g, ' ')
          const where = c.declared_in.length
            ? `Declared in ${c.declared_in.join(', ')}`
            : 'Declared in no repository you have scanned'
          const how = c.recognition === 'structural'
            ? ' Recognised by what the code does with it rather than by a product we know by name.'
            : ''
          const inherited = c.classification_inherited_from
            ? ` Classification inherited from ${c.classification_inherited_from}, not declared on the store itself.`
            : ''
          return (
            <Tooltip
              key={c.identity}
              title={`${label}. ${where}.${how}${inherited}${
                isDigest(c.identity) ? ' Address stored as a one-way digest.' : ''
              }`}
            >
              <Chip
                size="small"
                variant="outlined"
                label={shortIdentity(c.identity)}
                // Deliberately uncoloured. Amber and red mean severity
                // everywhere else in this product, and a classified store is a
                // fact rather than a problem — holding customer data in a
                // vector store is normal and correct. The problem is an
                // ungated agent reaching it, which is a finding and already
                // renders red through the risk overlay. Colouring the fact
                // teaches a reader that red sometimes means "bad" and
                // sometimes means "sensitive", which costs both meanings.
                sx={{ fontSize: '0.68rem', fontFamily: 'monospace' }}
              />
            </Tooltip>
          )
        })}
      </Stack>
      {(classified.length > 0 || knowledge.some(c => c.cross_repo)) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {classified.length > 0 && (
            <>
              {classified.length} carr{classified.length === 1 ? 'ies' : 'y'} a data
              classification{classified.length === 1 ? '' : 's'}
              {' '}({[...new Set(classified.map(c => c.classification))].join(', ')}).{' '}
            </>
          )}
          {knowledge.some(c => c.cross_repo) && (
            <>Declared outside this repository — a scan of either one alone cannot see this.</>
          )}
        </Typography>
      )}
    </Box>
  )
}

export default AgentReaches
